/**
 * Appear — fade + rise in on mount (optionally staggered by `index`). Starts
 * immediately on mount: no IntersectionObserver or scroll trigger can leave it
 * stuck at opacity 0 (the old "invisible card" failure the global CSS hacks
 * used to paper over). Reduced motion: renders fully visible at once.
 *
 *   <Appear index={i}>...</Appear>      // list item, 35ms stagger, capped
 *   <Appear from="scale">...</Appear>    // pop in (modals, badges)
 *   <Appear from="right" distance={14}>   // slide in from the side (rail rows)
 */
import React, { useEffect, useRef } from 'react';
import { Animated } from 'react-native';
import { timing, spring, staggerDelay, prefersReducedMotion } from '../../utils/motion';

export default function Appear({ children, index = 0, delay = 0, from = 'rise', distance = 8, style, ...rest }) {
    const reduced = prefersReducedMotion();
    const t = useRef(new Animated.Value(reduced ? 1 : 0)).current;

    useEffect(() => {
        if (reduced) return undefined;
        const wait = delay + staggerDelay(index);
        const anim = from === 'scale'
            ? Animated.sequence([Animated.delay(wait), spring(t, 1, 'pop')])
            : timing(t, 1, { duration: 'slow', easing: 'out', delay: wait });
        anim.start();
        return () => anim.stop();
        // Mount-only entrance.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const motionStyle = from === 'scale'
        ? { opacity: t.interpolate({ inputRange: [0, 0.6, 1], outputRange: [0, 1, 1] }), transform: [{ scale: t.interpolate({ inputRange: [0, 1], outputRange: [0.92, 1] }) }] }
        : from === 'fade'
            ? { opacity: t }
            : from === 'right' || from === 'left'
                ? { opacity: t, transform: [{ translateX: t.interpolate({ inputRange: [0, 1], outputRange: [from === 'right' ? distance : -distance, 0] }) }] }
                : { opacity: t, transform: [{ translateY: t.interpolate({ inputRange: [0, 1], outputRange: [distance, 0] }) }] };

    return (
        <Animated.View style={[style, motionStyle]} {...rest}>
            {children}
        </Animated.View>
    );
}
